import axios from 'axios';
import Vue from 'vue';
import router from '@/router/index';
import {
    Notification
} from 'element-ui';

import cookieHelper from '@/helpers/cookie/cookie';
import isLogged from '@/helpers/api/isLogged';
import $config from '@/helpers/config/index';

window.cookieBridge = new cookieHelper;

const isServerError = error => {
    return error.response.status === 501 || error.response.status === 502 || error.response.status === 503;
}
const isNotAuthorized = error => {
    return error.response.status === 401;
}

class AxiosInstance {
    constructor(axios, vue) {
        this.defaultInstance = axios;
        this.vue = vue;
        this.cookieBridge = window.cookieBridge;

        this.instance = {}
        this.headers = {};
        this.params = null;
        this.tokenManager = {
            link: null
        };
        this.init();
    }

    init() {
        const params = {};
        let status = true;

        params.access_token = this.cookieBridge.getCookie('access_token');
        params.refresh_token = this.cookieBridge.getCookie('refresh_token');
        params.expires_in = this.cookieBridge.getCookie('expires_in');

        if (this.isExpired()) {
            status = true;
        }

        this.setToken(status, params);
    }

    getToken() {
        return `Bearer ${this.params.access_token}`;
    }

    async get(url) {
        if (!await this.authCheck()) {
            return;
        }

        return await this.instance.get(url);
    }

    async post(url, form) {
        return await this.instance.post(url, form);
    }

    async put(url, form) {
        if (!await this.authCheck()) {
            return;
        }

        return await this.instance.put(url, form);
    }


    async delete(url) {
        if (!await this.authCheck()) {
            return;
        }

        return await this.instance.delete(url);
    }

    setToken(type = false, params) {
        let change = type;
        if (change) {
            this.params = params;
            this.setCookie();
            window.isAuthenticated = isLogged();
        }

        this.build();
    }

    catchError() {
        this.instance.interceptors.response.use(response => {
            return response;
        }, error => {
            if (isNotAuthorized(error)) {
                this.clearCookies();
                redirectToLogin();
            } else if (isServerError(error)) {
                Notification({
                    title: "Server error",
                    message: "We're sorry, we have technical works",
                    duration: 5000
                });
            }

            return error.response;
        });

        this.instance.interceptors.request.use(async config => {
            // if user not logged, try to refresh token
            // except some endpoints, such as signin, signup, autosignin etc.  
            if (!isLogged() && !isAllowed(config.url)) {
                await this.refreshToken();

                if (!isAuthenticated) {
                    redirectToLogin();
                }
            }

            config.headers['Authorization'] = 'Bearer ' + this.params?.access_token || '';
            return config;
        }, error => {
            redirectToLogin();
            return error;
        });
    }

    build() {
        this.instance = this.defaultInstance.create({
            baseURL: this.vue.prototype.$config.apiUrl
        });
        this.catchError();
    }

    async refreshToken() {
        const response = await this.post('/refresh_token', {
            refresh_token: this.cookieBridge.getCookie('refresh_token')
        });

        if (response.status === 200) {
            this.setToken(true, response.data);
        }
    }

    setCookie() {
        this.clearCookies();

        if ($config.key == 'prod') {
            document.cookie = `__access_token=${this.params.access_token};max-age=${this.params.expires_in};path=/;${getDomain()}`;
        }

        document.cookie = `access_token=${this.params.access_token}; max-age=${this.params.expires_in}; path=/;`;
        document.cookie = `refresh_token=${this.params.refresh_token}; max-age=${this.params.expires_in}; path=/;`;
        document.cookie = `expires_in=${this.params.expires_in}; max-age=${this.params.expires_in}; path=/;`;
    }

    async authCheck() {
        if (this.isExpired()) {
            redirectToLogin();
        }

        if (this.canRefresh()) {
            try {
                await this.refreshToken();
                return true;
            } catch (err) {
                return false;
            }
        }

        return true;
    }

    clearCookies() {
        this.cookieBridge.deleteCookie('access_token');
        this.cookieBridge.deleteCookie('refresh_token');
        this.cookieBridge.deleteCookie('expires_in');

        document.cookie = `__access_token=null;max-age=-1;path=/;${getDomain()}`;
    }

    isExpired() {
        return this.cookieBridge.getCookie('access_token') == undefined && this.cookieBridge.getCookie('refresh_token') == undefined;
    }

    canRefresh() {
        return this.cookieBridge.getCookie('access_token') == undefined && this.cookieBridge.getCookie('refresh_token') != undefined;
    }
}

function getDomain() {
    return $config.project == 'profy' ? 'domain=getprofy.de;' : 'domain=hilfy.at;'
}

function isAllowed(url) {
    const allowed = [
        '/signin', '/signup', '/refresh_token', '/auth/login',
        `${$config.originalApiUrl}/clients/autosignin`, `${$config.originalApiUrl}/confirmation`,
        '/phone/verification/verify', '/phone/verification/send-code', '/check',
        '/coordinates'
    ];

    return allowed.includes(url);
}

function onAuthLoginPage() {
    return window.location.href.match('/auth/login') != null;
}

function redirectToLogin() {
    if (!onAuthLoginPage()) {
        router.push('/auth/login');
    }
}

export function init() {
    return new AxiosInstance(axios, Vue);
}